<template>
    <div class="register-page-container darken">
        <div class="content">
            <img src="../assets/logo.svg" class="logo " alt="logo"/>

            <Form v-if="screen === 'signup'" @submit="signup" :disabled="is_processing" class="form-container signup-form">

                <p>{{ $t('auth.identity_verified') }}</p>
                <p>{{ $t('auth.setup_your_account') }}</p>

                <FormInputText v-model="$v.credentials.phone.$model" identifier="phone" :label="$t('users.phone')"
                               :placeholder="$t('users.phone')" :disabled="is_processing"
                               :has-error="$v.credentials.phone.$error">
                    <template v-slot:errors>
                        <p v-if="!$v.credentials.phone.required">
                            {{$t('validation.x_is_required',{x: $t('users.phone')})}}
                        </p>
                    </template>
                </FormInputText>

                <FormInputText v-model="$v.credentials.address.$model" identifier="address" :label="$t('users.address')"
                               :placeholder="$t('users.address')" :disabled="is_processing" :useTextarea="true"
                               :has-error="$v.credentials.address.$error">
                    <template v-slot:errors>
                        <p v-if="!$v.credentials.address.required">
                            {{$t('validation.x_is_required',{x: $t('users.address')})}}
                        </p>
                    </template>
                </FormInputText>

                <FormInputPassword v-model="$v.credentials.password.$model" identifier="password" :label="$t('users.password')"
                                   :placeholder="$t('users.password')" :disabled="is_processing" type="password"
                                   :has-error="$v.credentials.password.$error" autocomplete="off">
                    <template v-slot:errors>
                        <p v-if="!$v.credentials.password.required">
                            {{$t('validation.x_is_required',{x: $t('users.password')})}}
                        </p>
                        <p v-else-if="!$v.credentials.password.minLength">
                            {{ $t('validation.password_must_be_atleast_x_characters',{x: 8}) }}
                        </p>
                    </template>
                </FormInputPassword>

                <FormInputPassword v-model="$v.credentials.password_confirmation.$model" identifier="password_confirmation" :label="$t('users.confirm_password')"
                                   :placeholder="$t('users.confirm_password')" :disabled="is_processing" type="password"
                                   :has-error="$v.credentials.password_confirmation.$error" autocomplete="off">
                    <template v-slot:errors>
                        <p v-if="!$v.credentials.password_confirmation.required">
                            {{$t('validation.x_is_required',{x: $t('users.confirm_password')})}}
                        </p>
                        <p v-else-if="!$v.credentials.password_confirmation.passwordConfirmation">
                            {{$t('validation.passwords_must_match')}}
                        </p>
                        <p v-else-if="!$v.credentials.password.minLength">
                            {{ $t('validation.password_must_be_atleast_x_characters',{x: 8}) }}
                        </p>
                    </template>
                </FormInputPassword>

                <div class="submit-row">
                    <Button :disabled="is_processing" className="--primary" :class="{'spinner-black': is_processing}" type="submit">
                        {{$t('signup')}}
                    </Button>
                </div>
            </Form>
            <Form v-else-if="screen === 'signup_success'" class="success-form">
                <p>{{ $t('auth.account_created') }}</p>

                <font-awesome-icon :icon="['fal','check-circle']"/>
                <div class="submit-row">
                    <Button className="--primary" :onclick="goToLogin">
                        {{ $t('nav.login') }}
                    </Button>
                </div>
            </Form>
        </div>
    </div>
</template>

<script>
import { required, requiredIf, sameAs, minLength } from 'vuelidate/lib/validators';
import Button from "../components/Button";
import Form from "../components/form/Form";
import FormInputText from "../components/form/FormInputText";
import FormInputPassword from "../components/form/FormInputPassword";

export default {
    name: "register-page",
    components: {FormInputText, FormInputPassword, Form, Button},
    data() {
        return {
            credentials: {
                phone: '',
                address: '',
                password: '',
                password_confirmation: '',
            },
            token: null,
            screen: 'signup',
            show_password: false,
            is_validating: false,
            is_processing: false,
        }
    },
    validations: {
        credentials: {
            phone: {required},
            address: {required},
            password: { required, minLength: minLength(8) },
            password_confirmation: {
                required: requiredIf(function (u) {
                    return u.password;
                }),
                passwordConfirmation: sameAs('password'),
                minLength: minLength(8),
            },
        }
    },
    methods: {
        signup(){
            this.$v.credentials.$touch();
            if (this.$v.credentials.$anyError || this.is_processing)
                return;

            this.is_processing = true;
            this.$axios
                .post(
                    `organisations/register-organisation`,
                    {
                        token: this.token,
                        phone: this.credentials.phone,
                        address: this.credentials.address,
                        password: this.credentials.password,
                        password_confirmation: this.credentials.password_confirmation,
                    },
                    { headers: { Authorization: null } }
                )
                .then(({ data }) => {
                    this.$notify({
                        text: this.$t('auth.password_has_been_updated'),
                        type: 'success',
                    });

                    this.screen = 'signup_success'

                    this.is_processing = false;
                })
                .catch((e) => {
                    this.is_processing = false;

                    this.$notify({
                        title: this.$t('error'),
                        text: this.$larerror(e.response.data.errors, this.$t('auth.failed_signup')),
                        type: 'error',
                    });
                });
        },

        goToLogin() {
            this.$router.push({
                name: 'login',
                params: { locale: this.$store.state.locale },
            });
        },

        async validateToken() {
            this.is_validating = true;
            await this.$axios
                .post(
                    `organisations/invite/validate`,
                    {
                        token: this.token,
                    },
                    { headers: { Authorization: null } }
                )
                .then(({ data }) => {
                    this.is_validating = false;
                })
                .catch((e) => {
                    this.$notify({
                        title: this.$t('error'),
                        text: this.$larerror(e.response.data.errors, this.$t('auth.unauthorized')),
                        type: 'error',
                    });

                    this.$router.push({
                        name: 'login',
                        params: { locale: this.$store.state.locale },
                    });
                });
        },
    },
    async mounted() {
        // if (this.$route.query.token) {
        //     this.token = this.$route.query.token;
        //     await this.validateToken();
        // } else
        //     this.$router.push({
        //         name: 'login',
        //     });
    },

    head() {
        return {
            title: {
                inner: this.$t('nav.signup')
            },
        }
    }
}
</script>
<style lang="scss" scoped>
.register-page-container {
    @apply bg-cover bg-center bg-no-repeat bg-primary-over min-h-screen w-full flex flex-col justify-center items-center;

    .content {
        @apply px-10 py-8 rounded-lg flex flex-col max-w-full bg-white;

        width: 90vw;
        //background-color: rgba(0, 0, 0, 0.65);
        z-index: 1;

        @screen sm{
            width: 478px;
        }

        .logo {
            @apply w-auto mb-8 object-contain h-8;
        }

        .form-container {
            @apply bg-white;

            & > .input-group{
                margin-bottom: 1rem !important;

                input {
                    color: theme('colors.black') !important;
                    height: 50px;
                }

                .password-field{
                    height: 50px;

                    & > button{
                        @apply mt-1;
                    }
                }
            }

            .submit-row {
                @apply flex flex-row justify-between items-center;
                margin-top: 0rem !important;

                a.forgot {
                    @apply text-black text-sm font-bold no-underline cursor-pointer;
                }
            }
        }

        .signup-form {
            @apply flex flex-col items-center;

            & > p {
                @apply text-black font-medium leading-loose text-base text-center max-w-92;

                &:last-of-type {
                    @apply mb-4;
                }
            }

            .input-group {
                @apply flex flex-col items-start mb-4 w-full;

                & > label {
                    @apply text-sm block text-black font-bold mb-2 ml-4;

                    small {
                        @apply text-xs;
                    }
                }

                ::-webkit-input-placeholder { /* Edge */
                    color: theme('colors.black') !important;
                    font-weight: normal;
                    opacity: 1 !important;
                    font-size: 14px !important;
                }

                :-ms-input-placeholder { /* Internet Explorer 10-11 */
                    color: theme('colors.black') !important;
                    font-weight: normal;
                    opacity: 1 !important;
                    font-size: 14px !important;
                }

                ::placeholder {
                    color: theme('colors.black') !important;
                    font-weight: normal;
                    opacity: 1 !important;
                    font-size: 14px !important;
                }

                & > .input-wrapper, &.input-group-with-button > .input-button-wrapper > .input-wrapper {
                    @apply w-full flex flex-col;
                }

                .input-wrapper {
                    input, textarea {
                        @apply bg-transparent text-black border-2 border-primary p-4;

                        &:focus {
                            @apply border-primary outline-none;
                        }
                    }
                }

                &.error {
                    input, textarea, .password-field {
                        border-color: theme('colors.negative') !important;
                    }
                }

                &.input-group--password {
                    & > .input-wrapper {
                        @apply w-full flex flex-col border-0 rounded;

                        & > .password-field {
                            @apply rounded bg-transparent border-2 border-primary w-full p-0 flex flex-row items-center justify-between;

                            input {
                                @apply border-0 bg-transparent text-black text-base w-full appearance-none flex-1;
                            }

                            button {
                                @apply p-0 ml-4 mr-4;

                                &:active, &:focus {
                                    @apply outline-none;
                                }

                                svg {
                                    @apply cursor-pointer text-grey text-lg ml-4;
                                }
                            }

                            &:focus-within {
                                @apply border-primary;
                            }
                        }
                    }

                    ::-webkit-input-placeholder { /* Edge */
                        color: theme('colors.black') !important;
                        font-weight: normal;
                        opacity: 1 !important;
                        font-size: 14px !important;
                    }

                    :-ms-input-placeholder { /* Internet Explorer 10-11 */
                        color: theme('colors.black') !important;
                        font-weight: normal;
                        opacity: 1 !important;
                        font-size: 14px !important;
                    }

                    ::placeholder {
                        color: theme('colors.black') !important;
                        font-weight: normal;
                        opacity: 1 !important;
                        font-size: 14px !important;
                    }
                }

                .errors {
                    @apply mt-3 mb-1;

                    p {
                        @apply text-negative italic text-sm py-1 ml-4;
                    }
                }

                .submit-row {
                    @apply flex flex-row justify-between items-center w-full;

                    button {
                        @apply w-full;
                    }
                }
            }
        }

        .success-form {
            @apply flex flex-col items-center;

            & > p {
                @apply text-black font-medium text-base text-center max-w-92 mt-2;
            }

            svg{
                @apply text-secondary text-8xl my-12;
            }

            .submit-row {
                @apply flex flex-row justify-between items-center w-full;

                button{
                    @apply mx-auto cursor-pointer;
                }
            }
        }
    }
}
</style>
